import { Link, Typography } from '@mui/material';

import { formatCurrency } from '@/shared/lib/currency';

import type { InvoiceLink, PaymentLine } from './paymentLines';

interface PaymentBreakdownProps {
  lines: PaymentLine[];
  /** The merchant fee on the chosen method. Included in `total`; omitted from
   *  the list when the agent absorbs it, since a "$0.00" row invites the
   *  question of what it is. */
  processingFee: number;
  total: number;
  invoiceLinks: InvoiceLink[];
}

/**
 * The itemised total, above the pay button.
 *
 * A director paying an annual review is settling two separate fees plus a
 * processing fee that changes with how they pay, and should see all of it
 * before they commit — the legacy dialog showed the fees in one panel and the
 * processing fee inside whichever method row it applied to, so the number on
 * the button matched nothing on screen.
 */
export const PaymentBreakdown = ({
  lines,
  processingFee,
  total,
  invoiceLinks,
}: PaymentBreakdownProps) => (
  <div className="flex flex-col gap-3 rounded-lg border border-divider p-3">
    <dl className="m-0 flex flex-col gap-2">
      {lines.map((line) => (
        <div key={line.label} className="flex justify-between gap-4">
          <dt>
            <Typography variant="body2" color="text.secondary">
              {line.label}
            </Typography>
          </dt>
          <dd className="m-0">
            <Typography variant="body2" className="tabular-nums">
              {formatCurrency(line.amount)}
            </Typography>
          </dd>
        </div>
      ))}

      {processingFee > 0 && (
        <div className="flex justify-between gap-4">
          <dt>
            <Typography variant="body2" color="text.secondary">
              Processing fee
            </Typography>
          </dt>
          <dd className="m-0">
            <Typography variant="body2" className="tabular-nums">
              {formatCurrency(processingFee)}
            </Typography>
          </dd>
        </div>
      )}

      <div className="flex justify-between gap-4 border-t border-divider pt-2">
        <dt>
          <Typography variant="body2" className="font-bold">
            Total
          </Typography>
        </dt>
        <dd className="m-0">
          <Typography variant="body2" className="font-bold tabular-nums">
            {formatCurrency(total)}
          </Typography>
        </dd>
      </div>
    </dl>

    {invoiceLinks.length > 0 && (
      <ul className="m-0 flex list-none flex-col gap-1 border-t border-divider pt-2 pl-0">
        {invoiceLinks.map((invoice) => (
          <li key={invoice.id}>
            {/* min-h-11 keeps these at the 44px touch target: they sit close
                together, and a mis-tap opens the wrong document. */}
            <Link
              href={invoice.href}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              className="flex min-h-11 items-center"
            >
              {invoice.label}
            </Link>
          </li>
        ))}
      </ul>
    )}
  </div>
);
